import { inject, Injectable } from "@angular/core";
import { isResponseElement } from "projects/shared/src/lib/core/helpers/content-player-utils";
import { buildBaseAnswer } from "projects/shared/src/lib/core/helpers/exercise-store.helpers";
import { NormTestAnswer } from "projects/shared/src/lib/core/interfaces/answer";
import { ExerciseFlowAdapter } from "projects/shared/src/lib/core/interfaces/exercise-flow-adapter";
import { TaskItem } from "projects/shared/src/lib/core/interfaces/task-item";
import { TaskItemResponseElement } from "projects/shared/src/lib/core/interfaces/task-item-response-element";
import { TaskService } from "projects/shared/src/lib/core/services/task.service";
import { BuildAnswerParams, CompleteTaskDevParams, ForceEndTaskParams, LoadTaskFromItemParams, LoadTaskParams, ProcessAnswerParams } from "projects/shared/src/lib/core/types/exercise-flow.types";

@Injectable()
export class NormTestFlowAdapter implements ExerciseFlowAdapter {
    private readonly taskService = inject(TaskService);

    public async loadTask(params: LoadTaskParams) {
        if (params.mode !== 'norm-test') {
            throw new Error('NormTestFlowAdapter received invalid loadTask params');
        }

        return this.taskService.getNormTestTask(params.userNormTestId);
    }

    public async loadTaskFromItem(params: LoadTaskFromItemParams) {
        if (params.mode !== 'norm-test') {
            throw new Error('NormTestFlowAdapter received invalid loadTaskFromItem params');
        }

        return this.taskService.getNormTestTaskItem(params.taskId, params.itemId);
    }

    public buildAnswer(params: BuildAnswerParams): NormTestAnswer {
        return {
            ...buildBaseAnswer({
                ...params,
                isLastItem: params.context.isLastItem,
            }),
        };
    }

    public async processAnswer(params: ProcessAnswerParams): Promise<void> {
        const answer = this.buildAnswer(params);
        await this.taskService.processNormTestTaskAnswer(answer);
    }

    public async forceEndTask(params: ForceEndTaskParams): Promise<void> {
        const answers: NormTestAnswer[] = [];

        for (let i = params.currentIndex + 1; i < params.task.items.length; i++) {
            const item = params.task.items[i];
            const emptyAnswer: TaskItemResponseElement = {
                answer: '',
                correctAnswer: '',
                duration: 0,
                id: '',
                isCorrect: false,
            };

            answers.push(
                this.buildAnswer({
                    answers: [emptyAnswer],
                    taskResultId: params.task.taskResultId,
                    taskItemId: item.id,
                    isAttempt: false,
                    duration: 0,
                    resetLastActiveItemDuration: false,
                    context: {
                        ...params.context,
                        taskItemIndex: i,
                        isLastItem: i === params.task.items.length - 1,
                    },
                })
            );
        }

        if (answers.length) {
            await this.taskService.processNormTestTaskAnswers(answers);
        }
    }

    public async completeTaskDev(params: CompleteTaskDevParams): Promise<void> {
        const answers: NormTestAnswer[] = [];

        for (let i = params.currentIndex; i < params.task.items.length; i++) {
            const item: TaskItem = params.task.items[i];
            const correctAnswers = item.elements
                .filter((x) => isResponseElement(x.type))
                .map((x) => x.content)
                .join(',');

            const answer: TaskItemResponseElement = {
                answer: correctAnswers,
                correctAnswer: correctAnswers,
                duration: 1000,
                id: '',
                isCorrect: true,
            };

            answers.push(
                this.buildAnswer({
                    answers: [answer],
                    taskResultId: params.task.taskResultId,
                    taskItemId: item.id,
                    isAttempt: false,
                    duration: 1000,
                    resetLastActiveItemDuration: false,
                    context: {
                        ...params.context,
                        taskItemIndex: i,
                        isLastItem: i === params.task.items.length - 1,
                    },
                })
            );
        }

        if (answers.length) {
            await this.taskService.processNormTestTaskAnswers(answers);
        }
    }
}